import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, 
  TrendingUp, 
  Database, 
  AlertCircle, 
  CheckCircle, 
  RefreshCw,
  Info,
  Target,
  Percent,
  Activity
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModelMetricsTab = () => {
  const [performance, setPerformance] = useState(null);
  const [dataQuality, setDataQuality] = useState(null);
  const [modelInfo, setModelInfo] = useState(null);
  const [coinMetrics, setCoinMetrics] = useState(null);
  const [calibration, setCalibration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(30);

  useEffect(() => {
    loadAllMetrics();
  }, [selectedPeriod]);

  const loadAllMetrics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [perfRes, qualityRes, infoRes, coinRes, calibRes] = await Promise.all([
        axios.get(`${API}/model-metrics/performance?days=${selectedPeriod}`),
        axios.get(`${API}/model-metrics/data-quality`),
        axios.get(`${API}/model-metrics/model-info`),
        axios.get(`${API}/model-metrics/by-coin?limit=10`),
        axios.get(`${API}/model-metrics/confidence-calibration`)
      ]);
      
      setPerformance(perfRes.data);
      setDataQuality(qualityRes.data);
      setModelInfo(infoRes.data);
      setCoinMetrics(coinRes.data);
      setCalibration(calibRes.data);
    } catch (err) {
      console.error('Error loading metrics:', err);
      setError('Failed to load model metrics');
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, unit, description, icon: Icon, color = "blue" }) => (
    <div className="bg-black/30 border border-white/10 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground">{title}</span>
        <Icon className={`w-4 h-4 text-${color}-400`} />
      </div>
      <div className="flex items-baseline gap-1">
        <span className={`text-2xl font-bold text-${color}-400`}>
          {value !== null && value !== undefined ? value : 'N/A'}
        </span>
        {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
      </div>
      {description && (
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      )}
    </div>
  );

  const getWinRateColor = (rate) => {
    if (rate >= 55) return 'text-green-400';
    if (rate >= 45) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getCalibrationColor = (error) => {
    const absError = Math.abs(error);
    if (absError <= 5) return 'text-green-400';
    if (absError <= 10) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
        <span className="ml-2 text-muted-foreground">Loading model metrics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-red-400">
        <AlertCircle className="w-6 h-6 mr-2" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="model-metrics-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-400" />
            Model Performance Metrics
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Read-only analysis of model predictions vs actual outcomes
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(Number(e.target.value))}
            className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm"
            data-testid="period-selector"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={9999}>All time</option>
          </select>
          
          <button
            onClick={loadAllMetrics}
            className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Performance Metrics */}
      {performance?.status === 'success' && performance.metrics && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Target className="w-5 h-5 text-green-400" />
            Prediction Accuracy
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              title="Win Rate"
              value={performance.metrics.win_rate}
              unit="%"
              description="Profitable trades"
              icon={TrendingUp}
              color="green"
            />
            <MetricCard
              title="Direction Accuracy"
              value={performance.metrics.accuracy}
              unit="%"
              description="Correct BUY/SELL direction"
              icon={CheckCircle}
              color="blue"
            />
            <MetricCard
              title="MAE"
              value={performance.metrics.mae}
              unit="%"
              description="Mean Absolute Error"
              icon={Activity}
              color="yellow"
            />
            <MetricCard
              title="RMSE"
              value={performance.metrics.rmse}
              unit="%"
              description="Root Mean Squared Error"
              icon={Activity}
              color="orange"
            />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              title="MAPE"
              value={performance.metrics.mape}
              unit="%"
              description="Mean Abs % Error"
              icon={Percent}
              color="purple"
            />
            <MetricCard
              title="Mean Error (Bias)"
              value={performance.metrics.me}
              unit="%"
              description={performance.metrics.me > 0 ? "Underpredicting" : "Overpredicting"}
              icon={Info}
              color="cyan"
            />
            <MetricCard
              title="R² Score"
              value={performance.metrics.r2_score}
              unit=""
              description="Model fit quality"
              icon={BarChart3}
              color="indigo"
            />
            <MetricCard
              title="Sample Size"
              value={performance.sample_size}
              unit="trades"
              description={`Last ${performance.period_days} days`}
              icon={Database}
              color="gray"
            />
          </div>
          
          {/* Trade Stats */}
          {performance.trade_stats && (
            <div className="bg-black/20 border border-white/10 rounded-lg p-4 mt-4">
              <h4 className="text-sm font-medium mb-3">Trade Statistics</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Total Trades:</span>
                  <span className="ml-2 font-medium">{performance.trade_stats.total_trades}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Wins:</span>
                  <span className="ml-2 font-medium text-green-400">{performance.trade_stats.wins}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Losses:</span>
                  <span className="ml-2 font-medium text-red-400">{performance.trade_stats.losses}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Avg Confidence:</span>
                  <span className="ml-2 font-medium">{performance.trade_stats.avg_confidence}%</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Total P&L:</span>
                  <span className={`ml-2 font-medium ${performance.trade_stats.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ${performance.trade_stats.total_pnl?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {performance?.status !== 'success' && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
          <p className="text-yellow-400 text-sm">
            {performance?.message || 'No performance data available'}
          </p>
        </div>
      )}

      {/* Confidence Calibration */}
      {calibration?.status === 'success' && calibration.calibration?.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Percent className="w-5 h-5 text-purple-400" />
            Confidence Calibration
            <span className="text-xs text-muted-foreground ml-2">
              (Is 80% confidence really 80% accurate?)
            </span>
          </h3>
          
          <div className="bg-black/20 border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-black/30">
                <tr>
                  <th className="text-left p-3">Confidence Range</th>
                  <th className="text-center p-3">Expected Win Rate</th>
                  <th className="text-center p-3">Actual Win Rate</th>
                  <th className="text-center p-3">Calibration Error</th>
                  <th className="text-center p-3">Sample Size</th>
                </tr>
              </thead>
              <tbody>
                {calibration.calibration.map((row, idx) => (
                  <tr key={idx} className="border-t border-white/5">
                    <td className="p-3 font-medium">{row.confidence_range}%</td>
                    <td className="p-3 text-center text-muted-foreground">{row.expected_win_rate}%</td>
                    <td className={`p-3 text-center font-medium ${getWinRateColor(row.actual_win_rate)}`}>
                      {row.actual_win_rate}%
                    </td>
                    <td className={`p-3 text-center font-medium ${getCalibrationColor(row.calibration_error)}`}>
                      {row.calibration_error > 0 ? '+' : ''}{row.calibration_error}%
                    </td>
                    <td className="p-3 text-center text-muted-foreground">{row.sample_size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <p className="text-xs text-muted-foreground">
            Positive error = Model is underconfident (actual better than predicted).
            Negative error = Model is overconfident (actual worse than predicted).
          </p>
        </div>
      )}

      {/* Performance by Coin */}
      {coinMetrics?.status === 'success' && coinMetrics.coin_metrics?.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-orange-400" />
            Performance by Coin
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {coinMetrics.coin_metrics.map((coin, idx) => (
              <div 
                key={idx}
                className="bg-black/20 border border-white/10 rounded-lg p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{coin.coin}</span>
                  <span className={`text-sm font-bold ${getWinRateColor(coin.win_rate)}`}>
                    {coin.win_rate}%
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {coin.wins}/{coin.total_trades} wins
                </div>
                <div className={`text-xs ${coin.avg_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  Avg P&L: ${coin.avg_pnl}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Quality */}
      {dataQuality?.status === 'success' && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Database className="w-5 h-5 text-cyan-400" />
            Data Quality
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Signal History Stats */}
            <div className="bg-black/20 border border-white/10 rounded-lg p-4">
              <h4 className="text-sm font-medium mb-3">Signal History</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Records:</span>
                  <span className="font-medium">{dataQuality.signal_history?.total_records?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Data Completeness:</span>
                  <span className={`font-medium ${dataQuality.signal_history?.completeness >= 95 ? 'text-green-400' : 'text-yellow-400'}`}>
                    {dataQuality.signal_history?.completeness}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Unique Coins:</span>
                  <span className="font-medium">{dataQuality.signal_history?.unique_coins}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date Range:</span>
                  <span className="font-medium text-xs">
                    {dataQuality.date_range?.from?.split('T')[0]} to {dataQuality.date_range?.to?.split('T')[0]}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Signal Distribution */}
            <div className="bg-black/20 border border-white/10 rounded-lg p-4">
              <h4 className="text-sm font-medium mb-3">Signal Distribution</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">BUY Signals:</span>
                  <span className="font-medium text-green-400">
                    {dataQuality.signal_history?.signal_distribution?.BUY?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">SELL Signals:</span>
                  <span className="font-medium text-red-400">
                    {dataQuality.signal_history?.signal_distribution?.SELL?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Profitable:</span>
                  <span className="font-medium text-green-400">
                    {dataQuality.signal_history?.status_distribution?.closed_profit?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Losses:</span>
                  <span className="font-medium text-red-400">
                    {dataQuality.signal_history?.status_distribution?.closed_loss?.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Null Counts */}
          {dataQuality.signal_history?.null_counts && (
            <div className="bg-black/20 border border-white/10 rounded-lg p-4">
              <h4 className="text-sm font-medium mb-3">Null Value Analysis</h4>
              <div className="grid grid-cols-3 md:grid-cols-7 gap-3 text-xs">
                {Object.entries(dataQuality.signal_history.null_counts).map(([field, count]) => (
                  <div key={field} className="text-center">
                    <div className={`font-medium ${count === 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                      {count}
                    </div>
                    <div className="text-muted-foreground truncate">{field}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Model Info */}
      {modelInfo?.status === 'success' && modelInfo.models && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Info className="w-5 h-5 text-indigo-400" />
            Model Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(modelInfo.models).map(([key, model]) => (
              <div key={key} className="bg-black/20 border border-white/10 rounded-lg p-4">
                <h4 className="font-medium text-blue-400 mb-2">{model.name}</h4>
                <div className="space-y-1 text-xs">
                  <div className="text-muted-foreground">Type: {model.type}</div>
                  <div className="text-muted-foreground">Purpose: {model.purpose}</div>
                  {model.features && (
                    <div className="mt-2">
                      <div className="text-muted-foreground mb-1">Features:</div>
                      <div className="flex flex-wrap gap-1">
                        {model.features.slice(0, 4).map((f, i) => (
                          <span key={i} className="bg-white/5 px-2 py-0.5 rounded text-xs">
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-xs text-muted-foreground text-center pt-4 border-t border-white/10">
        Last updated: {performance?.calculated_at ? new Date(performance.calculated_at).toLocaleString() : 'N/A'}
        <span className="mx-2">|</span>
        All metrics are read-only calculations from historical data
      </div>
    </div>
  );
};

export default ModelMetricsTab;
